import React from "react"
import { Button, Col, Row, Table } from "reactstrap"
import Switch from "components/Shared/Switch"
import Flatpickr from "react-flatpickr"
import { daysOfWeek } from "components/Common/constants"
import { useTranslation } from "react-i18next"
import {
  formatCalendarDateString,
  parseCalendarDateStrings,
} from "utils/calendarDateUtils"

export const buildDefaultDeliveryDays = () =>
  daysOfWeek.map(dayOfWeek => ({
    dayOfWeek,
    enabled: true,
  }))

export const buildDefaultDeliveryTimeSlots = () => [
  {
    startTime: "09:00",
    endTime: "12:00",
    labelEn: "Morning",
    labelAr: "صباح",
  },
  {
    startTime: "12:00",
    endTime: "17:00",
    labelEn: "Afternoon",
    labelAr: "بعد الظهر",
  },
  {
    startTime: "17:00",
    endTime: "21:00",
    labelEn: "Evening",
    labelAr: "مساء",
  },
]

const isDayEnabled = value =>
  value === true || value === "true" || value === 1 || value === "1"

const normalizeDeliveryDays = (deliveryDaysOfWeek = []) => {
  const source = deliveryDaysOfWeek?.length
    ? deliveryDaysOfWeek
    : buildDefaultDeliveryDays()

  return source.map(day => ({
    dayOfWeek: day.dayOfWeek,
    enabled: isDayEnabled(day.enabled),
  }))
}

const normalizeDeliveryTimeSlots = (deliveryTimeSlots = []) => {
  const source = deliveryTimeSlots?.length
    ? deliveryTimeSlots
    : buildDefaultDeliveryTimeSlots()

  return source.map(slot => ({
    startTime: slot.startTime || "09:00",
    endTime: slot.endTime || "12:00",
    labelEn: slot.labelEn || "",
    labelAr: slot.labelAr || "",
  }))
}

const DeliveryScheduleSettings = ({
  orderCutOffTime,
  setOrderCutOffTime,
  deliveryDaysOfWeek,
  setDeliveryDaysOfWeek,
  holidaysCutOffEnabled,
  setHolidaysCutOffEnabled,
  holidayBlackoutDates,
  setHolidayBlackoutDates,
  deliveryTimeSlots,
  setDeliveryTimeSlots,
}) => {
  const { t } = useTranslation()

  const toggleDeliveryDay = dayOfWeek => {
    setDeliveryDaysOfWeek(prev => {
      const current = normalizeDeliveryDays(prev)
      return current.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, enabled: !day.enabled }
          : day
      )
    })
  }

  const updateDeliveryTimeSlot = (index, field, value) => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      return current.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot
      )
    })
  }

  const removeDeliveryTimeSlot = index => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      if (current.length <= 1) {
        return current
      }
      return current.filter((_, slotIndex) => slotIndex !== index)
    })
  }

  const addDeliveryTimeSlot = () => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      return [
        ...current,
        {
          startTime: "09:00",
          endTime: "12:00",
          labelEn: "",
          labelAr: "",
        },
      ]
    })
  }

  const normalizedDays = normalizeDeliveryDays(deliveryDaysOfWeek)
  const normalizedSlots = normalizeDeliveryTimeSlots(deliveryTimeSlots)

  return (
    <div className="mt-4">
      <h5 className="mb-3">{t("Delivery Schedule Settings")}</h5>
      <Row className="mb-3">
        <Col md={6}>
          <label className="form-label">{t("Order Cut-off Time")}</label>
          <input
            type="time"
            className="form-control"
            value={orderCutOffTime || "20:00"}
            onChange={e => setOrderCutOffTime(e.target.value)}
          />
        </Col>
      </Row>

      <div className="border rounded p-3 mb-4 bg-light">
        <h6 className="mb-1">{t("Delivery Time Slot Configuration")}</h6>
        <p className="text-muted small mb-3">
          {t(
            "Define the delivery time slots shown to customers when scheduling a delivery. Add each slot with its start time, end time, and display labels in English and Arabic."
          )}
        </p>

        <Table bordered responsive className="mb-3 bg-white">
          <thead>
            <tr>
              <th>{t("Slot Start Time")}</th>
              <th>{t("Slot End Time")}</th>
              <th>{t("Label (English)")}</th>
              <th>{t("Label (Arabic)")}</th>
              <th style={{ width: 60 }}>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {normalizedSlots.map((slot, index) => (
              <tr key={`delivery-slot-${index}`}>
                <td>
                  <input
                    type="time"
                    className="form-control"
                    value={slot.startTime}
                    onChange={e =>
                      updateDeliveryTimeSlot(index, "startTime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="time"
                    className="form-control"
                    value={slot.endTime}
                    onChange={e =>
                      updateDeliveryTimeSlot(index, "endTime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={slot.labelEn}
                    placeholder={t("e.g. Morning")}
                    onChange={e =>
                      updateDeliveryTimeSlot(index, "labelEn", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    dir="rtl"
                    value={slot.labelAr}
                    placeholder={t("e.g. صباح")}
                    onChange={e =>
                      updateDeliveryTimeSlot(index, "labelAr", e.target.value)
                    }
                  />
                </td>
                <td className="text-center align-middle">
                  {normalizedSlots.length > 1 ? (
                    <i
                      className="bx bxs-trash font-size-18 cursor-pointer text-danger"
                      title={t("Delete")}
                      onClick={() => removeDeliveryTimeSlot(index)}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Button
          type="button"
          color="primary"
          className="d-block ms-auto"
          onClick={addDeliveryTimeSlot}
        >
          {t("Add More Slot")}
        </Button>
      </div>

      <h6 className="mb-2">{t("Delivery Days of Week")}</h6>
      <Table bordered responsive className="mb-4">
        <thead>
          <tr>
            <th>{t("Day")}</th>
            <th>{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {normalizedDays.map(day => (
            <tr key={day.dayOfWeek}>
              <td>{t(day.dayOfWeek)}</td>
              <td>
                <Switch
                  isChecked={day.enabled}
                  onClickHandler={() => toggleDeliveryDay(day.dayOfWeek)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Row className="mb-3 align-items-center">
        <Col md={6}>
          <label className="form-label d-block">
            {t("Enable Holiday Blackout Dates")}
          </label>
          <Switch
            isChecked={!!holidaysCutOffEnabled}
            onClickHandler={() => setHolidaysCutOffEnabled(prev => !prev)}
          />
        </Col>
      </Row>

      {holidaysCutOffEnabled ? (
        <Row>
          <Col md={8}>
            <label className="form-label">{t("Holiday / Blackout Dates")}</label>
            <Flatpickr
              className="form-control"
              options={{
                mode: "multiple",
                dateFormat: "Y-m-d",
                defaultDate: parseCalendarDateStrings(holidayBlackoutDates),
              }}
              value={parseCalendarDateStrings(holidayBlackoutDates)}
              onChange={dates => {
                setHolidayBlackoutDates(
                  dates
                    .map(formatCalendarDateString)
                    .filter(Boolean)
                )
              }}
            />
          </Col>
        </Row>
      ) : null}
    </div>
  )
}

export default DeliveryScheduleSettings
